import * as React from "react"
import { Box, ListItem, Tooltip } from "@mui/material"
import { useRouter } from "next/router"
import Link from "next/link"
import { HiLockClosed } from "react-icons/hi"

export default function SidebarItem({
  href,
  icon: Icon,
  outlineIcon: OutlineIcon,
  rightAdornment: RightAdornment,
  title,
  children,
  activePaths,
  deemphasized,
  lastInSubmenu,
  collapse,
  isChild,
  hideIcon,
  sx,
  ...rest
}: {
  // activePathPattern is used to make sure the item stays open when you are clicking around in submenu items
  href: { pathname: string; query?: any; activePathPattern?: string }
  title: string
  icon?: React.ReactNode
  outlineIcon?: React.ReactNode
  rightAdornment?: React.ReactNode
  children?: React.ReactNode
  activePaths?: {
    path: string
    strategy: "exactPathname" | "startsWith" | "exactAsPath"
  }[]
  deemphasized?: boolean
  lastInSubmenu?: boolean
  collapse?: boolean
  isChild?: boolean
  hideIcon?: boolean
  sx?: any
}) {
  const router = useRouter()
  const activePathPatterToCompare = href?.activePathPattern || href?.pathname
  // logic for when to display submenu items in the sidebar
  const showChildren = children
    ? router.pathname.startsWith(activePathPatterToCompare)
    : false

  // logic for determining when to highlight a sidebar item
  let activePathList = activePaths || [
    {
      path: href?.pathname,
      strategy: "startsWith",
    },
  ]

  let itemIsActive = false
  activePathList.forEach(pathObj => {
    if (pathObj?.strategy === "exactPathname") {
      if (pathObj?.path === router.pathname) {
        itemIsActive = true
      }
    } else if (pathObj?.strategy === "startsWith") {
      if (router.pathname.startsWith(pathObj?.path)) {
        itemIsActive = true
      }
    } else if (pathObj?.strategy === "exactAsPath") {
      if (router.asPath === pathObj?.path) {
        itemIsActive = true
      }
    }
  })

  // hacky deletion of activePathPattern which isn't a type Next.js links can accept
  delete href.activePathPattern

  return (
    <>
      <ListItem
        disableGutters
        sx={{
          display: "flex",
          alignItems: collapse ? "center" : "inherit",
          py: 0,
          px: collapse ? 0.5 : 1.5,
          mb: lastInSubmenu ? 1 : 0,
          "&:hover": {
            bgcolor: itemIsActive
              ? "hsl(43deg 74% 18% / 7.5%)"
              : "hsl(40deg 78% 21% / 3%)",
            cursor: "pointer",
          },
          ...((itemIsActive && {
            bgcolor: "hsl(43deg 74% 18% / 3%)",
          }) ||
            {}),
          ...sx,
        }}
        {...rest}
      >
        <Link href={href} passHref={true}>
          <Tooltip title={collapse ? title : ""} placement="right">
            <Box
              component="a"
              color="info"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: collapse ? "center" : "flex-start",
                py: 0.5,
                px: 1.25,
                width: "100%",
                letterSpacing: 0,
                textTransform: "none",
                textDecoration: "none",
                borderRadius: 2,
                color: itemIsActive ? "primary.700" : "gray.500",
                ...(deemphasized && {
                  color: "gray.400",
                }),
                "&:hover": {
                  bgcolor: "transparent",
                },
              }}
            >
              <Box
                sx={{
                  display: hideIcon ? "none" : "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "20px",
                  height: "20px",
                  mr: collapse ? 0 : 1,
                  ...(isChild && {
                    visibility: {
                      xs: "hidden",
                      sm: "visible",
                      md: "hidden",
                    },
                  }),
                }}
              >
                {Icon && itemIsActive && Icon}
                {OutlineIcon && !itemIsActive && OutlineIcon}
              </Box>
              <Box
                sx={{
                  display: collapse ? "none" : "block",
                  width: "100%",
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  fontSize: "sm",
                  fontWeight: itemIsActive ? "600" : "medium",
                }}
              >
                {title}
                {RightAdornment && RightAdornment}
              </Box>
              {deemphasized && (
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "gray.300",
                    height: 12,
                  }}
                >
                  <HiLockClosed size={12} />
                </Box>
              )}
            </Box>
          </Tooltip>
        </Link>
      </ListItem>
      {showChildren && children}
    </>
  )
}
